import { useState } from 'react';
import { DateTime, T } from 'gt-react';
import { EnvelopeSimpleIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { FullMessageDialog } from '@/components/email-viewer/full-message-dialog';
import type { LabelingMailboxCopy } from '@erp/rpc';

const RECEIVED_AT = { dateStyle: 'medium', timeStyle: 'short' } as const;

interface Props {
  copies: LabelingMailboxCopy[];
}

/**
 * The same message as it landed in our other mailboxes (AIA-43), each openable.
 *
 * The review queue shows one item per message, so this is where the other
 * deliveries are reached — and where a reviewer sees that a copy is already on
 * a claim, which means the message has been answered.
 */
export function MailboxCopiesSection({ copies }: Props) {
  const [openMessageId, setOpenMessageId] = useState<string | null>(null);

  if (copies.length === 0) return null;

  return (
    <section className="space-y-1.5">
      <div className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        <T context="849 review section heading: the same email delivered to other mailboxes">
          Other mailbox copies
        </T>
      </div>
      <p className="text-xs text-muted-foreground">
        <T context="849 review hint: mailbox copies are one message, answered once">
          The same email, delivered to more than one mailbox. Answer it once.
        </T>
      </p>
      <div className="space-y-1.5">
        {copies.map((copy) => (
          <button
            key={copy.inboundEmailId}
            type="button"
            onClick={() => setOpenMessageId(copy.inboundEmailId)}
            className="flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left hover:bg-muted/50"
          >
            <EnvelopeSimpleIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-mono text-xs">
                {copy.mailbox ?? (
                  <T context="849 review: a mailbox copy that Graph gave no mailbox">
                    (no mailbox)
                  </T>
                )}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                <DateTime options={RECEIVED_AT}>
                  {new Date(copy.receivedAt)}
                </DateTime>
              </span>
            </span>
            {copy.linkedToClaim ? (
              <Badge variant="outline" className="shrink-0">
                <T context="849 review badge: this mailbox copy is already linked to a claim">
                  On a claim
                </T>
              </Badge>
            ) : null}
          </button>
        ))}
      </div>

      <FullMessageDialog
        inboundEmailId={openMessageId}
        onClose={() => setOpenMessageId(null)}
      />
    </section>
  );
}
